/**
 * 控制台组件
 *
 * 功能：
 * 1. 云台控制（方向/绝对位置/偏移角）
 * 2. 可见光相机设置
 * 3. 红外相机设置
 * 4. 激光测距仪设置
 * 5. 声音探测设置
 * 6. 通用功能设置
 *
 * 协议版本: V2.0 (v1.0.0_20260105)
 */

import { useState, useCallback, useRef } from 'react'
import {
  Button,
  Slider,
  Typography,
  message,
  Tooltip,
  InputNumber,
  Radio,
  Modal,
  Collapse,
  Switch,
  Select,
  Space,
  Popconfirm,
} from 'antd'
import {
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  LockOutlined,
  UnlockOutlined,
  AimOutlined,
  ControlOutlined,
  SendOutlined,
} from '@ant-design/icons'
import useWebSocket from '@/hooks/useWebSocket'
import { useDeviceStore, useUserStore } from '@/store/useDeviceStore'

const { Text } = Typography

interface PtzControllerProps {
  devId: string | null
}

// V2.0协议: 云台移动步长 (度)
const PTZ_STEP = 1.0

function PtzController({ devId }: PtzControllerProps) {
  const { sendControl, acquireLock, releaseLock } = useWebSocket()
  const { lockMap } = useDeviceStore()
  const { userId, isAdmin } = useUserStore()

  // Modal状态
  const [modalVisible, setModalVisible] = useState(false)

  // 悬浮按钮拖动状态
  const [buttonPos, setButtonPos] = useState({ bottom: 60, right: 30 })
  const [isDraggingButton, setIsDraggingButton] = useState(false)
  const buttonDragStart = useRef({ x: 0, y: 0 })

  // Modal拖动状态
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 })

  // ============================================================
  // 云台控制状态
  // ============================================================
  const [absAzimuth, setAbsAzimuth] = useState<number | null>(null)
  const [absElevation, setAbsElevation] = useState<number | null>(null)
  const [absRoll, setAbsRoll] = useState<number | null>(null)
  const [northOffset, setNorthOffset] = useState(10.0)
  const [cruiseAzimuthLeft, setCruiseAzimuthLeft] = useState<number | null>(null)
  const [cruiseAzimuthRight, setCruiseAzimuthRight] = useState<number | null>(null)
  const [cruiseElevationLower, setCruiseElevationLower] = useState<number | null>(null)
  const [cruiseElevationUpper, setCruiseElevationUpper] = useState<number | null>(null)

  // ============================================================
  // 可见光相机状态
  // ============================================================
  const [viWiper, setViWiper] = useState(0)
  const [viZoomOpt, setViZoomOpt] = useState(0)
  const [viZoomEle, setViZoomEle] = useState(0)
  const [viFocusAuto, setViFocusAuto] = useState(1)
  const [viFocusMode, setViFocusMode] = useState(0)
  const [viFocusOffset, setViFocusOffset] = useState(4.3)
  const [viDayNight, setViDayNight] = useState(1)
  const [viDeHeatHaze, setViDeHeatHaze] = useState(0)
  const [viNrLevel, setViNrLevel] = useState(1)
  const [viColouredFogging, setViColouredFogging] = useState(0)
  const [viOpticalFogging, setViOpticalFogging] = useState(0)
  const [viSuperFogging, setViSuperFogging] = useState(0)

  // ============================================================
  // 红外相机状态
  // ============================================================
  const [irZoomOpt, setIrZoomOpt] = useState(0)
  const [irZoomEle, setIrZoomEle] = useState(0)
  const [irBrightness, setIrBrightness] = useState(3)
  const [irContrast, setIrContrast] = useState(3)
  const [irPolarity, setIrPolarity] = useState(0)
  const [irMode, setIrMode] = useState(0)
  const [irDde, setIrDde] = useState(3)
  const [irRectification, setIrRectification] = useState(0)
  const [irCorrelation, setIrCorrelation] = useState(0)
  const [irHotspot, setIrHotspot] = useState(0)

  // ============================================================
  // 激光测距状态
  // ============================================================
  const [laserEnabled, setLaserEnabled] = useState(0)

  // ============================================================
  // 声音探测状态
  // ============================================================
  const [soundDetectEn, setSoundDetectEn] = useState(0)

  // ============================================================
  // 通用功能状态
  // ============================================================
  const [bitratePush, setBitratePush] = useState(2.0)
  const [bitrateSave, setBitrateSave] = useState(2.0)
  const [record, setRecord] = useState(0)
  const [osdEn, setOsdEn] = useState(0)
  const [srcType, setSrcType] = useState(0)
  const [delActSent, setDelActSent] = useState(false)

  // 获取当前设备的锁状态
  const lockState = devId ? lockMap[devId] : null
  const isLocked = lockState?.locked ?? false
  const isOwner = lockState?.owner_id === userId
  const canControl = isAdmin() && (!isLocked || isOwner)

  /**
   * 发送云台方向控制 (V2.0协议: 使用相对运动控制)
   */
  const handleDirection = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right') => {
      if (!devId || !canControl) {
        message.warning('无控制权限')
        return
      }

      const dirLabels = { up: '向上', down: '向下', left: '向左', right: '向右' }

      const params: Record<string, number> = {}

      switch (direction) {
        case 'up':
          params.up = PTZ_STEP
          break
        case 'down':
          params.down = PTZ_STEP
          break
        case 'left':
          params.left = PTZ_STEP
          break
        case 'right':
          params.right = PTZ_STEP
          break
      }

      sendControl(devId, params)
      message.info(`云台${dirLabels[direction]}移动`)
    },
    [devId, canControl, sendControl]
  )

  /**
   * 发送绝对位置
   */
  const handleSendPosition = useCallback(() => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    const params: Record<string, number> = {}
    if (absAzimuth !== null) params.azimuth = absAzimuth
    if (absElevation !== null) params.elevation = absElevation
    if (absRoll !== null) params.roll = absRoll

    if (Object.keys(params).length === 0) {
      message.warning('请至少设置一个角度值')
      return
    }

    sendControl(devId, params)
    message.success('已发送位置指令')
  }, [devId, canControl, absAzimuth, absElevation, absRoll, sendControl])

  /**
   * 发送正北偏移角
   */
  const handleSendNorthOffset = useCallback(() => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { north_offset: northOffset })
    message.success('已发送偏移角')
  }, [devId, canControl, northOffset, sendControl])

  /**
   * 启动定点巡航。巡航轨迹由设备端按协议执行。
   */
  const handleStartFixedPointCruise = useCallback(() => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }

    const values = [
      cruiseAzimuthLeft,
      cruiseAzimuthRight,
      cruiseElevationLower,
      cruiseElevationUpper,
    ]
    if (values.some((value) => value === null)) {
      message.warning('请填写定点巡航的全部边界角度')
      return
    }
    if (cruiseAzimuthLeft! < 0 || cruiseAzimuthLeft! > 360) {
      message.warning('方位边界范围为0°至360°')
      return
    }
    if (cruiseAzimuthRight! < 0 || cruiseAzimuthRight! > 360) {
      message.warning('方位边界范围为0°至360°')
      return
    }
    if (!((cruiseElevationLower! >= 0 && cruiseElevationLower! <= 90) || (cruiseElevationLower! >= 270 && cruiseElevationLower! <= 359))) {
      message.warning('俯仰边界必须在0°至90°或270°至359°')
      return
    }
    if (!((cruiseElevationUpper! >= 0 && cruiseElevationUpper! <= 90) || (cruiseElevationUpper! >= 270 && cruiseElevationUpper! <= 359))) {
      message.warning('俯仰边界必须在0°至90°或270°至359°')
      return
    }

    sendControl(devId, {
      fixed_point_cruise: {
        azimuth_left: cruiseAzimuthLeft,
        azimuth_right: cruiseAzimuthRight,
        elevation_lower: cruiseElevationLower,
        elevation_upper: cruiseElevationUpper,
        elevation_step: 10,
      },
    })
    message.success('已启动定点巡航')
  }, [
    devId,
    canControl,
    cruiseAzimuthLeft,
    cruiseAzimuthRight,
    cruiseElevationLower,
    cruiseElevationUpper,
    sendControl,
  ])

  /**
   * 申请/释放控制权
   */
  const handleLockToggle = useCallback(() => {
    if (!devId) return

    if (isOwner) {
      releaseLock(devId)
      message.info('已释放控制权')
    } else {
      acquireLock(devId)
      message.info('正在申请控制权...')
    }
  }, [devId, isOwner, acquireLock, releaseLock])

  /**
   * 悬浮按钮拖动处理
   */
  const handleButtonMouseDown = (e: React.MouseEvent) => {
    setIsDraggingButton(true)
    buttonDragStart.current = {
      x: e.clientX,
      y: e.clientY,
    }

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - buttonDragStart.current.x
      const deltaY = moveEvent.clientY - buttonDragStart.current.y

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        setButtonPos(prev => ({
          bottom: Math.max(10, prev.bottom - deltaY),
          right: Math.max(10, prev.right - deltaX),
        }))
        buttonDragStart.current = {
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        }
      }
    }

    const handleMouseUp = (upEvent: MouseEvent) => {
      const deltaX = upEvent.clientX - buttonDragStart.current.x
      const deltaY = upEvent.clientY - buttonDragStart.current.y

      if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) {
        setModalVisible(true)
      }

      setIsDraggingButton(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // ============================================================
  // 辅助函数: 发送可见光/红外/激光/通用参数
  // ============================================================

  const sendViConf = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { vi_conf: params })
    message.success('可见光参数已发送')
  }, [devId, canControl, sendControl])

  const sendIrConf = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { ir_conf: params })
    message.success('红外参数已发送')
  }, [devId, canControl, sendControl])

  const sendLaserConf = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { laser_conf: params })
    message.success('激光参数已发送')
  }, [devId, canControl, sendControl])

  const sendSoundConf = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { sound_conf: params })
    message.success('声音探测参数已发送')
  }, [devId, canControl, sendControl])

  const sendStreamConf = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, { stream_conf: params })
    message.success('通用参数已发送')
  }, [devId, canControl, sendControl])

  const sendTopLevel = useCallback((params: Record<string, unknown>) => {
    if (!devId || !canControl) {
      message.warning('无控制权限')
      return
    }
    sendControl(devId, params)
  }, [devId, canControl, sendControl])

  // ============================================================
  // 行内样式复用
  // ============================================================
  const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, color: '#595959', flexShrink: 0 }
  const sliderRowStyle: React.CSSProperties = { marginBottom: 8 }

  if (!devId) {
    return null
  }

  // ============================================================
  // Collapse面板内容
  // ============================================================

  const collapseItems = [
    // ---- 面板1: 云台控制 ----
    {
      key: 'ptz',
      label: <Text strong style={{ fontSize: 13 }}>云台控制</Text>,
      children: (
        <div style={{ display: 'flex', gap: 16 }}>
          {/* 左列: 方向控制盘 */}
          <div style={{ flex: '0 0 100px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center' }}>
            {/* 方向控制盘 */}
            <div className="ptz-controller" style={{ transform: 'scale(0.65)' }}>
              <Tooltip title="向上">
                <div
                  className="ptz-button"
                  style={{ top: 10, left: '50%', transform: 'translateX(-50%)' }}
                  onClick={() => handleDirection('up')}
                >
                  <UpOutlined />
                </div>
              </Tooltip>
              <Tooltip title="向下">
                <div
                  className="ptz-button"
                  style={{ bottom: 10, left: '50%', transform: 'translateX(-50%)' }}
                  onClick={() => handleDirection('down')}
                >
                  <DownOutlined />
                </div>
              </Tooltip>
              <Tooltip title="向左">
                <div
                  className="ptz-button"
                  style={{ left: 10, top: '50%', transform: 'translateY(-50%)' }}
                  onClick={() => handleDirection('left')}
                >
                  <LeftOutlined />
                </div>
              </Tooltip>
              <Tooltip title="向右">
                <div
                  className="ptz-button"
                  style={{ right: 10, top: '50%', transform: 'translateY(-50%)' }}
                  onClick={() => handleDirection('right')}
                >
                  <RightOutlined />
                </div>
              </Tooltip>
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 40,
                  height: 40,
                  background: '#f0f2f5',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AimOutlined style={{ fontSize: 18, color: '#999' }} />
              </div>
            </div>
          </div>

          {/* 右列: 位置/偏移 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 绝对位置 */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 11, color: '#8c8c8c' }}>方位</Text>
                <InputNumber
                  size="small"
                  value={absAzimuth}
                  onChange={(v) => setAbsAzimuth(v)}
                  style={{ width: 70 }}
                  placeholder="°"
                  disabled={!canControl}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 11, color: '#8c8c8c' }}>俯仰</Text>
                <InputNumber
                  size="small"
                  value={absElevation}
                  onChange={(v) => setAbsElevation(v)}
                  style={{ width: 70 }}
                  placeholder="°"
                  disabled={!canControl}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>横滚</Text>
                <InputNumber
                  size="small"
                  value={absRoll}
                  onChange={(v) => setAbsRoll(v)}
                  style={{ width: 70 }}
                  placeholder="°"
                  disabled
                />
              </div>
            </div>
            <Button
              size="small"
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendPosition}
              disabled={!canControl}
              block
            >
              发送位置
            </Button>

            <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

            {/* 正北偏移角 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text type="secondary" style={labelStyle}>正北偏移角:</Text>
              <InputNumber
                size="small"
                value={northOffset}
                onChange={(v) => setNorthOffset(v ?? 0)}
                style={{ flex: 1 }}
                step={0.1}
                disabled
              />
              <Button
                size="small"
                type="primary"
                onClick={handleSendNorthOffset}
                disabled
              >
                发送
              </Button>
            </div>

            <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

            {/* 一键控制 */}
            <div style={{ marginBottom: 4 }}>
              <Text style={labelStyle}>一键控制:</Text>
            </div>
            <Space size="small" wrap style={{ width: '100%' }}>
              <Button
                size="small"
                onClick={() => { sendTopLevel({ one_key_cruise: 0 }); message.info('云台回零位') }}
                disabled={!canControl}
              >
                回零位
              </Button>
              <Button
                size="small"
                onClick={() => { sendTopLevel({ one_key_cruise: 1 }); message.info('水平180°巡航') }}
                disabled={!canControl}
              >
                180°巡航
              </Button>
              <Button
                size="small"
                onClick={() => { sendTopLevel({ one_key_cruise: 2 }); message.info('水平360°巡航') }}
                disabled={!canControl}
              >
                360°巡航
              </Button>
            </Space>

            <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

            <div style={{ marginBottom: 4 }}>
              <Text style={labelStyle}>定点巡航:</Text>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6, marginBottom: 6 }}>
              <InputNumber
                size="small"
                value={cruiseAzimuthLeft}
                onChange={setCruiseAzimuthLeft}
                min={0}
                max={360}
                placeholder="方位边界1(0-360°)"
                disabled={!canControl}
                style={{ width: '100%' }}
              />
              <InputNumber
                size="small"
                value={cruiseAzimuthRight}
                onChange={setCruiseAzimuthRight}
                min={0}
                max={360}
                placeholder="方位边界2(0-360°)"
                disabled={!canControl}
                style={{ width: '100%' }}
              />
              <InputNumber
                size="small"
                value={cruiseElevationLower}
                onChange={setCruiseElevationLower}
                min={0}
                max={359}
                placeholder="俯仰边界1(0-90或270-359°)"
                disabled={!canControl}
                style={{ width: '100%' }}
              />
              <InputNumber
                size="small"
                value={cruiseElevationUpper}
                onChange={setCruiseElevationUpper}
                min={0}
                max={359}
                placeholder="俯仰边界2(0-90或270-359°)"
                disabled={!canControl}
                style={{ width: '100%' }}
              />
            </div>
            <Button
              size="small"
              type="primary"
              icon={<ControlOutlined />}
              onClick={handleStartFixedPointCruise}
              disabled={!canControl}
              block
            >
              定点巡航
            </Button>
          </div>
        </div>
      ),
    },

    // ---- 面板2: 可见光相机设置 ----
    {
      key: 'vi',
      label: <Text strong style={{ fontSize: 13 }}>可见光相机设置</Text>,
      children: (
        <div>
          {/* 雨刮 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>雨刮:</Text>
            <Radio.Group
              size="small"
              value={viWiper}
              onChange={(e) => { setViWiper(e.target.value); sendViConf({ wiper: e.target.value }) }}
              disabled={!canControl}
              buttonStyle="solid"
            >
              <Radio.Button value={0}>关</Radio.Button>
              <Radio.Button value={1}>单次</Radio.Button>
              <Radio.Button value={2}>10次</Radio.Button>
              <Radio.Button value={3}>持续</Radio.Button>
            </Radio.Group>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

          {/* 光学变倍 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>光学变倍 (zoom_opt):</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{viZoomOpt}x</Text>
            </div>
            <Slider
              min={0}
              max={30}
              value={viZoomOpt}
              onChange={setViZoomOpt}
              onChangeComplete={(v) => sendViConf({ zoom_opt: v })}
              disabled={!canControl}
            />
          </div>
          {/* 电子变倍 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>电子变倍:</Text>
            <Switch
              size="small"
              checked={viZoomEle === 1}
              onChange={(v) => { setViZoomEle(v ? 1 : 0); sendViConf({ zoom_ele: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 查询变倍 */}
          <div style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => sendViConf({ zoom_query: 1 })} disabled={!canControl}>
              查询变倍
            </Button>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

          {/* 自动聚焦 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>自动聚焦:</Text>
            <Switch
              size="small"
              checked={viFocusAuto === 1}
              onChange={(v) => { setViFocusAuto(v ? 1 : 0); sendViConf({ focus_auto: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 聚焦方向 (手动聚焦时) */}
          <div style={rowStyle}>
            <Text style={labelStyle}>聚焦方向:</Text>
            <Radio.Group
              size="small"
              value={viFocusMode}
              onChange={(e) => { setViFocusMode(e.target.value); sendViConf({ focus_mode: e.target.value }) }}
              disabled={!canControl || viFocusAuto === 1}
              buttonStyle="solid"
            >
              <Radio.Button value={0}>近</Radio.Button>
              <Radio.Button value={1}>远</Radio.Button>
            </Radio.Group>
          </div>
          {/* 聚焦偏移 (手动聚焦时) */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>聚焦偏移:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{viFocusOffset.toFixed(1)}</Text>
            </div>
            <Slider
              min={4.3}
              max={129}
              step={0.1}
              value={viFocusOffset}
              onChange={setViFocusOffset}
              onChangeComplete={(v) => sendViConf({ focus_offset: v })}
              disabled={!canControl || viFocusAuto === 1}
            />
          </div>
          {/* 查询聚焦 & 单次自动聚焦 */}
          <Space size="small" style={{ marginBottom: 8 }}>
            <Button size="small" onClick={() => sendViConf({ focus_query: 1 })} disabled={!canControl}>
              查询聚焦
            </Button>
            <Button size="small" onClick={() => sendViConf({ one_push_af: 1 })} disabled={!canControl}>
              单次自动聚焦
            </Button>
          </Space>

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

          {/* 白天/夜晚 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>日夜模式:</Text>
            <Radio.Group
              size="small"
              value={viDayNight}
              onChange={(e) => { setViDayNight(e.target.value); sendViConf({ day_night_node: e.target.value }) }}
              disabled={!canControl}
              buttonStyle="solid"
            >
              <Radio.Button value={1}>白天</Radio.Button>
              <Radio.Button value={2}>夜晚</Radio.Button>
            </Radio.Group>
          </div>
          {/* 去热雾 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>去热雾:</Text>
            <Select
              size="small"
              value={viDeHeatHaze}
              onChange={(v) => { setViDeHeatHaze(v); sendViConf({ de_heat_haze: v }) }}
              disabled={!canControl}
              style={{ width: 100 }}
              options={[
                { value: 0, label: '关闭' },
                { value: 1, label: '等级1' },
                { value: 2, label: '等级2' },
              ]}
            />
          </div>
          {/* 降噪等级 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>降噪等级:</Text>
            <Radio.Group
              size="small"
              value={viNrLevel}
              onChange={(e) => { setViNrLevel(e.target.value); sendViConf({ nr_level: e.target.value }) }}
              disabled={!canControl}
              buttonStyle="solid"
            >
              <Radio.Button value={1}>等级1</Radio.Button>
              <Radio.Button value={2}>等级2</Radio.Button>
            </Radio.Group>
          </div>
          {/* 彩色透雾 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>彩色透雾:</Text>
            <Switch
              size="small"
              checked={viColouredFogging === 1}
              onChange={(v) => { setViColouredFogging(v ? 1 : 0); sendViConf({ coloured_fogging: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 光学透雾 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>光学透雾:</Text>
            <Switch
              size="small"
              checked={viOpticalFogging === 1}
              onChange={(v) => { setViOpticalFogging(v ? 1 : 0); sendViConf({ optical_fogging: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 超级透雾 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>超级透雾:</Text>
            <Switch
              size="small"
              checked={viSuperFogging === 1}
              onChange={(v) => { setViSuperFogging(v ? 1 : 0); sendViConf({ super_fogging: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
        </div>
      ),
    },

    // ---- 面板3: 红外相机设置 ----
    {
      key: 'ir',
      label: <Text strong style={{ fontSize: 13 }}>红外相机设置</Text>,
      children: (
        <div>
          {/* 光学变倍 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>光学变倍 (zoom_opt):</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{irZoomOpt}x</Text>
            </div>
            <Slider
              min={0}
              max={30}
              value={irZoomOpt}
              onChange={setIrZoomOpt}
              onChangeComplete={(v) => sendIrConf({ zoom_opt: v })}
              disabled={!canControl}
            />
          </div>
          {/* 电子变倍 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>电子变倍:</Text>
            <Switch
              size="small"
              checked={irZoomEle === 1}
              onChange={(v) => { setIrZoomEle(v ? 1 : 0); sendIrConf({ zoom_ele: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

          {/* 亮度 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>亮度:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{irBrightness}</Text>
            </div>
            <Slider
              min={1}
              max={5}
              value={irBrightness}
              onChange={setIrBrightness}
              onChangeComplete={(v) => sendIrConf({ brightness: v })}
              disabled={!canControl}
            />
          </div>
          {/* 对比度 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>对比度:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{irContrast}</Text>
            </div>
            <Slider
              min={1}
              max={5}
              value={irContrast}
              onChange={setIrContrast}
              onChangeComplete={(v) => sendIrConf({ contrast: v })}
              disabled={!canControl}
            />
          </div>
          {/* 极性 (伪彩) */}
          <div style={rowStyle}>
            <Text style={labelStyle}>极性:</Text>
            <Select
              size="small"
              value={irPolarity}
              onChange={(v) => { setIrPolarity(v); sendIrConf({ polarity: v }) }}
              disabled={!canControl}
              style={{ width: 130 }}
              options={[
                { value: 0, label: '白热' },
                { value: 1, label: '黑热' },
                { value: 2, label: '红热伪彩' },
                { value: 3, label: '铁红伪彩' },
                { value: 4, label: '天空' },
                { value: 5, label: '边缘' },
                { value: 6, label: '狩猎' },
              ]}
            />
          </div>
          {/* 模式 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>场景模式:</Text>
            <Select
              size="small"
              value={irMode}
              onChange={(v) => { setIrMode(v); sendIrConf({ mode: v }) }}
              disabled={!canControl}
              style={{ width: 100 }}
              options={[
                { value: 0, label: '高亮' },
                { value: 1, label: '城市' },
                { value: 2, label: '野外' },
                { value: 3, label: '雨雾' },
              ]}
            />
          </div>
          {/* DDE */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>DDE:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{irDde}</Text>
            </div>
            <Slider
              min={1}
              max={5}
              value={irDde}
              onChange={setIrDde}
              onChangeComplete={(v) => sendIrConf({ dde: v })}
              disabled={!canControl}
            />
          </div>
          {/* 校正 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>校正:</Text>
            <Switch
              size="small"
              checked={irRectification === 1}
              onChange={(v) => { setIrRectification(v ? 1 : 0); sendIrConf({ rectification: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 关联 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>关联:</Text>
            <Switch
              size="small"
              checked={irCorrelation === 1}
              onChange={(v) => { setIrCorrelation(v ? 1 : 0); sendIrConf({ correlation: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* 热点 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>热点:</Text>
            <Switch
              size="small"
              checked={irHotspot === 1}
              onChange={(v) => { setIrHotspot(v ? 1 : 0); sendIrConf({ hotspot: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
        </div>
      ),
    },

    // ---- 面板4: 激光测距仪 ----
    {
      key: 'laser',
      label: <Text strong style={{ fontSize: 13 }}>激光测距仪</Text>,
      children: (
        <div>
          <div style={rowStyle}>
            <Text style={labelStyle}>测距开关:</Text>
            <Switch
              size="small"
              checked={laserEnabled === 1}
              onChange={(v) => {
                const newVal = v ? 1 : 0
                setLaserEnabled(newVal)
                sendLaserConf({ laser_en: newVal })
              }}
              disabled={!canControl}
            />
          </div>
          <div style={rowStyle}>
            <Text style={labelStyle}>测距模式:</Text>
            <Space size="small">
              <Button
                size="small"
                onClick={() => sendLaserConf({ laser_en: 1, laser_mode: 0 })}
                disabled={!canControl || laserEnabled !== 1}
              >
                单次
              </Button>
              <Button
                size="small"
                onClick={() => sendLaserConf({ laser_en: 1, laser_mode: 1 })}
                disabled={!canControl || laserEnabled !== 1}
              >
                连续
              </Button>
            </Space>
          </div>
        </div>
      ),
    },

    // ---- 面板5: 声音探测设置 ----
    {
      key: 'sound',
      label: <Text strong style={{ fontSize: 13 }}>声音探测设置</Text>,
      children: (
        <div>
          <div style={rowStyle}>
            <Text style={labelStyle}>声音识别:</Text>
            <Switch
              size="small"
              checked={soundDetectEn === 1}
              onChange={(v) => {
                const newVal = v ? 1 : 0
                setSoundDetectEn(newVal)
                sendSoundConf({ detect_en: newVal })
              }}
              disabled={!canControl}
            />
          </div>
        </div>
      ),
    },

    // ---- 面板6: 通用功能 ----
    {
      key: 'general',
      label: <Text strong style={{ fontSize: 13 }}>通用功能</Text>,
      children: (
        <div>
          {/* 推流码率 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>推流码率:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{bitratePush.toFixed(1)} MHz</Text>
            </div>
            <Slider
              min={0.5}
              max={10}
              step={0.1}
              value={bitratePush}
              onChange={setBitratePush}
              onChangeComplete={(v) => sendStreamConf({ bitratepu_push: v })}
              disabled={!canControl}
            />
          </div>
          {/* 保存码率 */}
          <div style={sliderRowStyle}>
            <div style={rowStyle}>
              <Text style={labelStyle}>保存码率:</Text>
              <Text style={{ fontSize: 12, color: '#1890ff' }}>{bitrateSave.toFixed(1)} MHz</Text>
            </div>
            <Slider
              min={0.5}
              max={10}
              step={0.1}
              value={bitrateSave}
              onChange={setBitrateSave}
              onChangeComplete={(v) => sendStreamConf({ bitratepu_save: v })}
              disabled={!canControl}
            />
          </div>
          {/* 录像 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>录像:</Text>
            <Switch
              size="small"
              checked={record === 1}
              onChange={(v) => { setRecord(v ? 1 : 0); sendStreamConf({ record: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>
          {/* OSD */}
          <div style={rowStyle}>
            <Text style={labelStyle}>OSD叠加:</Text>
            <Switch
              size="small"
              checked={osdEn === 1}
              onChange={(v) => { setOsdEn(v ? 1 : 0); sendStreamConf({ osd_en: v ? 1 : 0 }) }}
              disabled={!canControl}
            />
          </div>

          {/* 推流源 */}
          <div style={rowStyle}>
            <Text style={labelStyle}>推流源:</Text>
            <Space size="small">
              <Select
                size="small"
                value={srcType}
                onChange={(v) => { setSrcType(v); sendTopLevel({ src_type: v }) }}
                disabled={!canControl}
                style={{ width: 100 }}
                options={[
                  { value: 0, label: '可见光' },
                  { value: 1, label: '红外' },
                  { value: 2, label: '声' },
                  { value: 3, label: '无线' },
                ]}
              />
              <Button
                size="small"
                onClick={() => { setSrcType(99); sendTopLevel({ src_type: 99 }); message.info('已切换为同推（默认）') }}
                disabled={!canControl}
              >
                同推（默认）
              </Button>
            </Space>
          </div>

          <div style={{ borderTop: '1px solid #f0f0f0', margin: '8px 0' }} />

          {/* 设备自检 */}
          <Space size="small" wrap>
            <Button
              size="small"
              onClick={() => { sendTopLevel({ self_detect: 1 }); message.info('已发送自检指令') }}
              disabled={!canControl}
            >
              设备自检
            </Button>
            <Button
              size="small"
              onClick={() => { sendTopLevel({ soft_version: 1 }); message.info('已发送版本查询') }}
              disabled={!canControl}
            >
              查询版本
            </Button>
            {/* 删除数据: 先发 del_act，确认后发 del_confirm */}
            {!delActSent ? (
              <Button
                size="small"
                danger
                onClick={() => { sendTopLevel({ del_act: 1 }); setDelActSent(true); message.info('请确认删除') }}
                disabled={!canControl}
              >
                删除数据
              </Button>
            ) : (
              <Popconfirm
                title="确认删除设备数据？"
                description="此操作不可撤销"
                onConfirm={() => { sendTopLevel({ del_confirm: 1 }); setDelActSent(false); message.success('已确认删除') }}
                onCancel={() => setDelActSent(false)}
                okText="确认"
                cancelText="取消"
              >
                <Button size="small" danger type="primary" disabled={!canControl}>
                  确认删除
                </Button>
              </Popconfirm>
            )}
          </Space>
        </div>
      ),
    },
  ]

  return (
    <>
      {/* 悬浮可拖动按钮 */}
      <div
        onMouseDown={handleButtonMouseDown}
        style={{
          position: 'fixed',
          bottom: buttonPos.bottom,
          right: buttonPos.right,
          zIndex: 1000,
          cursor: isDraggingButton ? 'grabbing' : 'grab',
          transition: isDraggingButton ? 'none' : 'all 0.3s',
        }}
      >
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<ControlOutlined />}
          style={{
            width: 56,
            height: 56,
            fontSize: 24,
            boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
          }}
        />
      </div>

      {/* 控制台弹窗 */}
      <Modal
        title={
          <div
            style={{ cursor: 'move', userSelect: 'none' }}
            onMouseDown={(e) => {
              const startX = e.clientX - modalPos.x
              const startY = e.clientY - modalPos.y

              const handleMouseMove = (moveEvent: MouseEvent) => {
                setModalPos({
                  x: moveEvent.clientX - startX,
                  y: moveEvent.clientY - startY,
                })
              }

              const handleMouseUp = () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleMouseUp)
              }

              document.addEventListener('mousemove', handleMouseMove)
              document.addEventListener('mouseup', handleMouseUp)
            }}
          >
            控制台
          </div>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={560}
        mask={false}
        modalRender={(modal) => (
          <div
            style={{
              transform: `translate(${modalPos.x}px, ${modalPos.y}px)`,
            }}
          >
            {modal}
          </div>
        )}
        destroyOnClose
      >
        <div style={{ padding: '0 4px' }}>
          {/* 控制权状态 */}
          <div style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            padding: '8px 12px',
            background: '#fafafa',
            borderRadius: 6
          }}>
            {isLocked ? (
              <>
                <LockOutlined style={{ color: '#faad14', fontSize: 14 }} />
                <Text type="warning" style={{ fontSize: 12 }}>
                  {isOwner ? '您已获取控制权' : `由 ${lockState?.owner_name} 控制中`}
                </Text>
              </>
            ) : (
              <>
                <UnlockOutlined style={{ color: '#52c41a', fontSize: 14 }} />
                <Text type="success" style={{ fontSize: 12 }}>控制权空闲</Text>
              </>
            )}
            {isAdmin() && (
              <Button
                type={isOwner ? 'default' : 'primary'}
                icon={isOwner ? <UnlockOutlined /> : <LockOutlined />}
                onClick={handleLockToggle}
                danger={isOwner}
                size="small"
              >
                {isOwner ? '释放' : '申请'}
              </Button>
            )}
          </div>

          {/* 可折叠面板 */}
          <Collapse
            defaultActiveKey={['ptz']}
            items={collapseItems}
            size="small"
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          />
        </div>
      </Modal>
    </>
  )
}

export default PtzController
